import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Target, TrendingUp, AlertTriangle, BookOpen } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface SimStats {
  totalSimulations: number;
  avgScore: number;
  passRate: number;
  avgDuration: number;
  weakChapters: { chapter: number; avgScore: number }[];
  killerQuestions: { id: string; text: string; chapter: number; errorRate: number }[];
}

export const SimulationAnalyticsPage: React.FC = () => {
  const [stats, setStats] = useState<SimStats>({ totalSimulations: 0, avgScore: 0, passRate: 0, avgDuration: 25, weakChapters: [], killerQuestions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [usersRes, questionsRes] = await Promise.all([
        adminDb.from('users').select('data'),
        adminDb.from('questions').select('id, question, chapter_id'),
      ]);

      const users = usersRes.data || [];
      const questions = questionsRes.data || [];

      let totalSims = 0, totalScore = 0, passed = 0;
      const chapterScores: Record<number, number[]> = {};
      const questionErrors: Record<string, number> = {};

      users.forEach(u => {
        const exams = u.data?.examHistory || [];
        if (Array.isArray(exams)) {
          exams.forEach((exam: any) => {
            totalSims++;
            const score = exam.score || exam.correctPercentage || 0;
            totalScore += score;
            if (score >= 80) passed++;

            // Track weak chapters
            if (exam.chapterScores && typeof exam.chapterScores === 'object') {
              Object.entries(exam.chapterScores).forEach(([ch, s]) => {
                const chNum = parseInt(ch);
                if (!chapterScores[chNum]) chapterScores[chNum] = [];
                chapterScores[chNum].push(s as number);
              });
            }

            // Track wrong answers for killer questions
            if (Array.isArray(exam.wrongAnswers)) {
              exam.wrongAnswers.forEach((qId: string) => {
                questionErrors[qId] = (questionErrors[qId] || 0) + 1;
              });
            }
          });
        }
      });

      // Weak chapters
      const weakChapters = Object.entries(chapterScores)
        .map(([ch, scores]) => ({ chapter: parseInt(ch), avgScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) }))
        .sort((a, b) => a.avgScore - b.avgScore)
        .slice(0, 5);

      // Killer questions
      const killerQuestions = Object.entries(questionErrors)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([qId, errors]) => {
          const q = questions.find(qu => qu.id === qId);
          return { id: qId, text: q?.question?.substring(0, 60) || qId.substring(0, 8), chapter: q?.chapter_id || 0, errorRate: errors };
        });

      setStats({
        totalSimulations: totalSims, avgScore: totalSims > 0 ? Math.round(totalScore / totalSims) : 0,
        passRate: totalSims > 0 ? Math.round((passed / totalSims) * 100) : 0,
        avgDuration: 25, weakChapters, killerQuestions,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><ClipboardCheck size={24} /> Simulationsanalysen</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header"><h1><ClipboardCheck size={24} /> Simulationsanalysen</h1></div>

      <div className="grid-cols-4 mb-6">
        <div className="kpi-card"><span className="kpi-label">Simulationen gesamt</span><span className="kpi-value">{stats.totalSimulations}</span></div>
        <div className="kpi-card"><span className="kpi-label">Ø Ergebnis</span><span className="kpi-value" style={{ color: stats.avgScore >= 80 ? 'var(--success)' : 'var(--warning)' }}>{stats.avgScore}%</span></div>
        <div className="kpi-card"><span className="kpi-label">Bestehensquote</span><span className="kpi-value" style={{ color: stats.passRate >= 70 ? 'var(--success)' : 'var(--danger)' }}>{stats.passRate}%</span></div>
        <div className="kpi-card"><span className="kpi-label">Ø Dauer</span><span className="kpi-value">{stats.avgDuration} min</span></div>
      </div>

      <div className="grid-cols-2 mb-6">
        {/* Pass Rate Gauge */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}><Target size={18} /> Bestehensquote</h3>
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ width: 140, height: 140, borderRadius: '50%', margin: '0 auto 16px', border: `6px solid ${stats.passRate >= 70 ? 'var(--success)' : stats.passRate >= 50 ? 'var(--warning)' : 'var(--danger)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <span style={{ fontSize: 36, fontWeight: 800 }}>{stats.passRate}%</span>
              <span className="text-xs text-muted">bestanden</span>
            </div>
            <p className="text-sm text-muted">
              {stats.passRate >= 80 ? 'Hervorragend! Hohe Bestehensquote.' : stats.passRate >= 60 ? 'Gut, aber Verbesserungspotenzial.' : stats.totalSimulations === 0 ? 'Noch keine Daten vorhanden.' : 'Aufmerksamkeit noetig.'}
            </p>
          </div>
        </div>

        {/* Weak Chapters */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}><BookOpen size={18} /> Schwache Kapitel</h3>
          {stats.weakChapters.length > 0 ? (
            <div className="flex flex-col gap-3">
              {stats.weakChapters.map(ch => (
                <div key={ch.chapter} className="flex items-center gap-3">
                  <span className="text-sm" style={{ width: 80 }}>Kapitel {ch.chapter}</span>
                  <div style={{ flex: 1, background: 'var(--border-light)', borderRadius: 4, height: 20, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ background: ch.avgScore >= 70 ? 'var(--success)' : ch.avgScore >= 50 ? 'var(--warning)' : 'var(--danger)', height: '100%', borderRadius: 4, width: `${ch.avgScore}%` }} />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 600 }}>{ch.avgScore}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info"><AlertTriangle size={14} /><span className="text-sm">Daten zu schwachen Kapiteln werden nach genügend Simulationen angezeigt.</span></div>
          )}
        </div>
      </div>

      {/* Killer Questions in Exams */}
      {stats.killerQuestions.length > 0 && (
        <div className="card mb-6">
          <h3 style={{ marginBottom: 16 }}><AlertTriangle size={18} /> Killer-Fragen in Simulationen</h3>
          <table>
            <thead><tr><th>#</th><th>Frage</th><th>Kap.</th><th>Fehler</th></tr></thead>
            <tbody>
              {stats.killerQuestions.map((q, i) => (
                <tr key={q.id}>
                  <td>{i + 1}</td>
                  <td className="text-sm">{q.text}...</td>
                  <td><span className="badge badge-neutral">{q.chapter}</span></td>
                  <td style={{ fontWeight: 700, color: 'var(--danger)' }}>{q.errorRate}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stats.totalSimulations === 0 && (
        <div className="alert alert-info"><AlertTriangle size={14} /><span>Noch keine Simulationen vorhanden. Daten werden automatisch angezeigt sobald Nutzer Prüfungen ablegen.</span></div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
